import type { Coords } from '@ixigo/cab-core';
import type { LocationIdentifier } from '@/components/search-form/api/get-location';

export interface UrlPrefillParams {
	pickup?: LocationIdentifier;
	drop?: LocationIdentifier;
	time?: string;
}

/**
 * Parses URL search parameters for prefilling the search form
 *
 * Supported formats for pickup/drop:
 * 1. Comma-separated coordinates (recommended): ?pickup=28.5562,77.1000
 * 2. PlaceId: ?pickup=ChIJL_P_CXMEDTkRw0ZdG-0GVvw
 * 3. JSON coordinates (legacy): ?pickup={"latitude":28.5562,"longitude":77.1000}
 *
 * @param searchParams - URLSearchParams from the current URL
 * @returns Parsed prefill parameters or null if no valid params found
 */
export function parseUrlPrefillParams(
	searchParams: URLSearchParams,
): UrlPrefillParams | null {
	const pickupParam = searchParams.get('pickup');
	const dropParam = searchParams.get('drop');
	const timeParam = searchParams.get('time');

	// Return null if no params are present
	if (!pickupParam && !dropParam && !timeParam) {
		return null;
	}

	const result: UrlPrefillParams = {};

	// Parse pickup location identifier
	if (pickupParam) {
		const pickup = parseLocationIdentifier(pickupParam);
		if (pickup) {
			result.pickup = pickup;
		}
	}

	// Parse drop location identifier
	if (dropParam) {
		const drop = parseLocationIdentifier(dropParam);
		if (drop) {
			result.drop = drop;
		}
	}

	// Parse time (validate ISO string)
	if (timeParam && isValidISODateString(timeParam)) {
		result.time = timeParam;
	}

	// Return null if no valid params were parsed
	if (!result.pickup && !result.drop && !result.time) {
		return null;
	}

	return result;
}

/**
 * Parses location identifier from string - supports multiple formats:
 * 1. Comma-separated coordinates: "28.5562,77.1000" (latitude,longitude)
 * 2. PlaceId: "ChIJL_P_CXMEDTkRw0ZdG-0GVvw" (Google Place ID)
 * 3. JSON coordinates: {"latitude":28.5562,"longitude":77.1000}
 *
 * @param locationString - Location string in any supported format
 * @returns LocationIdentifier object or null if invalid
 */
function parseLocationIdentifier(
	locationString: string,
): LocationIdentifier | null {
	// Try JSON format for coordinates
	if (locationString.startsWith('{')) {
		const coords = parseJsonCoordinates(locationString);
		if (coords) {
			return { coordinates: coords };
		}
	}

	// Try comma-separated coordinates
	if (locationString.includes(',')) {
		const coords = parseCommaSeparatedCoordinates(locationString);
		if (coords) {
			return { coordinates: coords };
		}
	}

	// Treat as placeId if it's a non-empty string that doesn't look like coordinates
	if (isValidPlaceId(locationString)) {
		return { placeId: locationString };
	}

	return null;
}

/**
 * Parses comma-separated coordinates: "28.5562,77.1000"
 */
function parseCommaSeparatedCoordinates(coordString: string): Coords | null {
	const parts = coordString.split(',');
	if (parts.length !== 2 || !parts[0] || !parts[1]) {
		return null;
	}

	const lat = parseFloat(parts[0].trim());
	const lng = parseFloat(parts[1].trim());

	if (
		!Number.isNaN(lat) &&
		!Number.isNaN(lng) &&
		isValidLatitude(lat) &&
		isValidLongitude(lng)
	) {
		return { lat, lng };
	}

	return null;
}

/**
 * Parses JSON coordinates: {"latitude":28.5562,"longitude":77.1000}
 */
function parseJsonCoordinates(jsonString: string): Coords | null {
	try {
		const parsed = JSON.parse(jsonString);
		if (
			typeof parsed === 'object' &&
			parsed !== null &&
			typeof parsed.latitude === 'number' &&
			typeof parsed.longitude === 'number' &&
			isValidLatitude(parsed.latitude) &&
			isValidLongitude(parsed.longitude)
		) {
			return { lat: parsed.latitude, lng: parsed.longitude };
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Validates if a string looks like a valid Google Place ID
 * Place IDs are alphanumeric strings, typically starting with "ChIJ" or similar
 */
function isValidPlaceId(placeId: string): boolean {
	// PlaceId should be non-empty, alphanumeric with underscores/hyphens
	// Typically 20-50 characters, but we'll be lenient
	return (
		placeId.length >= 10 &&
		placeId.length <= 200 &&
		/^[a-zA-Z0-9_-]+$/.test(placeId)
	);
}

/**
 * Validates if a string is a valid ISO 8601 date string (e.g. 2026-05-01T12:00:00Z)
 */
function isValidISODateString(dateString: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}T/.test(dateString)) return false;
	const date = new Date(dateString);
	return !Number.isNaN(date.getTime());
}

/**
 * Validates latitude value (-90 to 90)
 */
function isValidLatitude(lat: number): boolean {
	return lat >= -90 && lat <= 90;
}

/**
 * Validates longitude value (-180 to 180)
 */
function isValidLongitude(lng: number): boolean {
	return lng >= -180 && lng <= 180;
}
